import { useEffect, useState, useRef } from 'react'

/**
 * DeviceApprovalQR
 *
 * Renders on the new (unauthenticated) device.
 * 1. Calls POST /api/auth/device/init → gets sessionId + approveUrl + challengeType
 * 2. Renders QR encoding approveUrl
 * 3. Opens SSE /api/auth/device/poll?s=sessionId
 * 4. On approved: calls onSuccess(token)
 */

interface Props {
  onSuccess: (token: string) => void
  onCancel?: () => void
}

interface DeviceSession {
  sessionId:     string
  approveUrl:    string
  challengeType: string
  expiresAt:     number
  ttl:           number
  deviceHint:    string
}

async function generateQRDataURL(text: string): Promise<string> {
  try {
    const QRCode = (await import('qrcode')).default
    return QRCode.toDataURL(text, {
      width:  240,
      margin: 2,
      color: { dark: '#60A5FA', light: '#0A0F1A' }, // blue on dark (matches Entry Protocol)
    })
  } catch {
    return `https://chart.googleapis.com/chart?cht=qr&chs=240x240&chl=${encodeURIComponent(text)}&chco=60A5FA&chf=bg,s,0A0F1A`
  }
}

const CHALLENGE_LABELS: Record<string, string> = {
  biometric:   'Face ID / Touch ID',
  symbol:      'Constellation symbol',
  wallet_code: '6-digit pass code',
  pattern:     'Tap pattern',
}

export default function DeviceApprovalQR({ onSuccess, onCancel }: Props) {
  const [session,    setSession]   = useState<DeviceSession | null>(null)
  const [qrDataUrl,  setQrDataUrl] = useState<string | null>(null)
  const [countdown,  setCountdown] = useState(0)
  const [status,     setStatus]    = useState<'loading' | 'ready' | 'approved' | 'denied' | 'locked' | 'error'>('loading')
  const sseRef = useRef<EventSource | null>(null)

  const initSession = async () => {
    setStatus('loading')
    try {
      const res  = await fetch('/api/auth/device/init', { method: 'POST' })
      const data = await res.json()
      if (!data.ok) throw new Error(data.error)

      setSession(data)
      setCountdown(data.ttl)

      const qr = await generateQRDataURL(data.approveUrl)
      setQrDataUrl(qr)
      setStatus('ready')
      openSSE(data.sessionId)
    } catch {
      setStatus('error')
    }
  }

  const openSSE = (sessionId: string) => {
    sseRef.current?.close()
    const es = new EventSource(`/api/auth/device/poll?s=${sessionId}`)
    es.addEventListener('approved', (e) => {
      const d = JSON.parse((e as MessageEvent).data)
      setStatus('approved')
      es.close()
      onSuccess(d.token)
    })
    es.addEventListener('denied',  () => { setStatus('denied');  es.close() })
    es.addEventListener('locked',  () => { setStatus('locked');  es.close() })
    es.addEventListener('expired', () => { es.close(); initSession() })
    es.onerror = () => { es.close() }
    sseRef.current = es
  }

  useEffect(() => { initSession(); return () => sseRef.current?.close() }, [])

  useEffect(() => {
    if (!session || status !== 'ready') return
    const interval = setInterval(() => {
      const rem = Math.max(0, session.expiresAt - Math.floor(Date.now() / 1000))
      setCountdown(rem)
      if (rem === 0) clearInterval(interval)
    }, 1000)
    return () => clearInterval(interval)
  }, [session, status])

  const mm = String(Math.floor(countdown / 60)).padStart(2, '0')
  const ss = String(countdown % 60).padStart(2, '0')

  return (
    <div style={{
      display:       'flex',
      flexDirection: 'column',
      alignItems:    'center',
      gap:           '20px',
      padding:       '32px 24px',
      background:    '#0A0F1A',
      borderRadius:  '16px',
      border:        '1px solid rgba(96,165,250,0.15)',
      maxWidth:      '320px',
      margin:        '0 auto',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center' }}>
        <p style={{
          fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize:      '8px',
          letterSpacing: '0.35em',
          textTransform: 'uppercase',
          color:         'rgba(96,165,250,0.5)',
          margin:        '0 0 8px',
        }}>
          Approve from another device
        </p>
        <h3 style={{
          fontFamily:  '"Helvetica Neue", Helvetica, Arial, sans-serif',
          fontSize:    '18px',
          fontWeight:  100,
          color:       'rgba(255,255,255,0.85)',
          margin:      0,
          letterSpacing: '0.02em',
        }}>
          Scan with your phone
        </h3>
      </div>

      {/* QR */}
      <div style={{
        width:         '240px',
        height:        '240px',
        background:    '#0A0F1A',
        border:        '1px solid rgba(96,165,250,0.2)',
        borderRadius:  '12px',
        display:       'flex',
        alignItems:    'center',
        justifyContent: 'center',
        overflow:      'hidden',
      }}>
        {status === 'loading' && (
          <div style={{
            width: '18px', height: '18px',
            border: '1px solid rgba(96,165,250,0.2)',
            borderTopColor: 'rgba(96,165,250,0.7)',
            borderRadius: '50%', animation: 'dev-spin 0.8s linear infinite',
          }} />
        )}
        {status === 'ready' && qrDataUrl && (
          <img src={qrDataUrl} alt="Approve on your phone" width={220} height={220}
               style={{ borderRadius: '8px' }} />
        )}
        {status === 'approved' && (
          <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
            <circle cx="22" cy="22" r="20" stroke="rgba(96,165,250,0.4)" strokeWidth="1"/>
            <path d="M13 22l7 7 11-14" stroke="#60A5FA" strokeWidth="1.5"
                  strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {(status === 'denied' || status === 'locked') && (
          <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
            <circle cx="22" cy="22" r="20" stroke="rgba(239,68,68,0.4)" strokeWidth="1"/>
            <path d="M15 15l14 14M29 15l-14 14" stroke="rgba(239,68,68,0.7)" strokeWidth="1.5"
                  strokeLinecap="round"/>
          </svg>
        )}
      </div>

      {/* Challenge hint */}
      {status === 'ready' && session && (
        <p style={{
          fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize:      '8px',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color:         'rgba(255,255,255,0.3)',
          textAlign:     'center',
          margin:        0,
          lineHeight:    1.8,
        }}>
          Your phone will ask for:&nbsp;
          <span style={{ color: 'rgba(96,165,250,0.6)' }}>
            {CHALLENGE_LABELS[session.challengeType] ?? session.challengeType}
          </span>
        </p>
      )}

      {/* Instructions */}
      {status === 'ready' && (
        <p style={{
          fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize:      '8px',
          letterSpacing: '0.15em',
          color:         'rgba(100,116,139,0.45)',
          textAlign:     'center',
          margin:        0,
          lineHeight:    1.8,
        }}>
          Open LUKHΛS on your phone and<br/>scan this code, or visit your saved link
        </p>
      )}

      {/* Status messages */}
      {status === 'denied' && (
        <p style={{ fontFamily: 'ui-monospace', fontSize: '9px', color: 'rgba(239,68,68,0.6)',
          letterSpacing: '0.2em', textTransform: 'uppercase', margin: 0 }}>
          Access denied
        </p>
      )}
      {status === 'locked' && (
        <p style={{ fontFamily: 'ui-monospace', fontSize: '9px', color: 'rgba(239,68,68,0.6)',
          letterSpacing: '0.2em', textTransform: 'uppercase', margin: 0 }}>
          Too many attempts — session locked
        </p>
      )}

      {/* Countdown */}
      {status === 'ready' && (
        <p style={{
          fontFamily:    'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize:      '10px',
          letterSpacing: '0.15em',
          color:         countdown < 60 ? 'rgba(239,68,68,0.6)' : 'rgba(100,116,139,0.4)',
          margin:        0,
        }}>
          Expires in {mm}:{ss}
        </p>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px' }}>
        {(status === 'error' || status === 'denied' || status === 'locked') && (
          <button onClick={initSession} style={{
            fontFamily: 'ui-monospace', fontSize: '8px', letterSpacing: '0.2em',
            textTransform: 'uppercase', color: 'rgba(96,165,250,0.7)', background: 'none',
            border: '1px solid rgba(96,165,250,0.2)', padding: '6px 12px',
            borderRadius: '4px', cursor: 'pointer',
          }}>
            Try again
          </button>
        )}
        {onCancel && (
          <button onClick={onCancel} style={{
            fontFamily: 'ui-monospace', fontSize: '8px', letterSpacing: '0.2em',
            textTransform: 'uppercase', color: 'rgba(100,116,139,0.5)', background: 'none',
            border: 'none', cursor: 'pointer', padding: '6px 0',
          }}>
            Cancel
          </button>
        )}
      </div>

      <style>{`@keyframes dev-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
